/**
 * Watchlist - customizable column set + full-expand overlay.
 *
 * Columns are defined once in WATCHLIST_COLUMNS (single source of truth for
 * both the grid and the Customize Columns modal). Each column has a stable
 * id, a label, a cell class, a get() that derives the value from WatchlistRow
 * with a deterministic per-symbol seeded fallback, and an optional format().
 *
 * When a live WS stream starts feeding per-symbol fundamentals, the row
 * object simply grows new fields; no column-registry changes are needed
 * because every get() has a graceful fallback.
 */
import { useEffect, useMemo, useState } from 'react';

export interface WatchlistRow {
  symbol: string;
  mark: number;
  pctChange: number;
  volume?: number;
  bid?: number; ask?: number; bidSize?: number; askSize?: number;
  last?: number; dailyHigh?: number; dailyLow?: number; yesterdayClose?: number;
  eps?: number; pe?: number; marketCapKobo?: string; shares?: number;
  sector?: string; industry?: string; subIndustry?: string; description?: string;
  beta?: number; iv?: number; yield?: number; dividendAmt?: number;
  exDate?: string; payDate?: string; upcomingEarnings?: string; lastTradeDate?: string;
  delta?: number; gamma?: number; theta?: number; vega?: number; rho?: number;
  openInterest?: number; daysToExp?: number;
  htbEtb?: 'HTB' | 'ETB'; approxBorrow?: number; initialMargin?: number;
  shortSaleBorrow?: number; putCallRatio?: number; ivPercentile?: number;
  mktMakerMove?: number; epr?: number;
}

interface Props {
  rows: WatchlistRow[];
  selected?: string;
  onSelect: (sym: string) => void;
}

type ColCls = 'sym' | 'num' | 'num mono' | 'num vol' | 'text';
interface ColDef {
  id: string;
  label: string;
  cls: ColCls;
  get: (r: WatchlistRow) => number | string | undefined;
  format?: (v: number | string | undefined, r: WatchlistRow) => React.ReactNode;
}

function seedh(sym: string, salt: number): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < sym.length; i++) h = Math.imul(h ^ sym.charCodeAt(i), 16777619) >>> 0;
  h = Math.imul(h ^ salt, 16777619) >>> 0;
  return h;
}
const seededNum = (sym: string, salt: number, min: number, max: number) =>
  min + (seedh(sym, salt) % 100000) / 100000 * (max - min);
const seededInt = (sym: string, salt: number, min: number, max: number) =>
  Math.floor(seededNum(sym, salt, min, max));

function fmtPct(v: number | string | undefined): React.ReactNode {
  if (typeof v !== 'number') return '-';
  return (<span className={v >= 0 ? 'up' : 'down'}>{(v >= 0 ? '+' : '') + v.toFixed(2)}%</span>);
}
function fmtNum(v: number | string | undefined, digits = 2): React.ReactNode {
  if (typeof v !== 'number') return '-';
  return v.toFixed(digits);
}
function fmtNaira(v: number | string | undefined): React.ReactNode {
  if (typeof v !== 'number') return '-';
  return '\u20A6' + v.toLocaleString('en-NG', { maximumFractionDigits: 2 });
}
function fmtVol(v: number | string | undefined): React.ReactNode {
  if (typeof v !== 'number') return '-';
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + 'M';
  if (v >= 1_000) return (v / 1_000).toFixed(1) + 'K';
  return v.toString();
}
function fmtStr(v: number | string | undefined): React.ReactNode {
  if (v == null || v === '') return '-';
  return String(v);
}

export const WATCHLIST_COLUMNS: ColDef[] = [
  { id: 'mark',       label: 'Mark',            cls: 'num mono', get: r => r.mark, format: v => fmtNum(v) },
  { id: 'markChgPct', label: 'Mark Chg %',      cls: 'num',      get: r => r.pctChange, format: fmtPct },
  { id: 'volume',     label: 'Volume',          cls: 'num vol',  get: r => r.volume ?? seededInt(r.symbol, 10, 80_000, 2_000_000), format: fmtVol },
  { id: 'bid',        label: 'Bid',             cls: 'num mono', get: r => r.bid ?? r.mark * (1 - 0.0008), format: v => fmtNum(v) },
  { id: 'ask',        label: 'Ask',             cls: 'num mono', get: r => r.ask ?? r.mark * (1 + 0.0008), format: v => fmtNum(v) },
  { id: 'iv',         label: 'Implied Volatil', cls: 'num',      get: r => r.iv ?? seededNum(r.symbol, 1, 12, 55), format: v => typeof v === 'number' ? v.toFixed(1) + '%' : '-' },
  { id: 'low52',      label: '52 Week Low',     cls: 'num mono', get: r => seededNum(r.symbol, 2, r.mark * 0.6, r.mark * 0.9), format: v => fmtNum(v) },
  { id: 'high52',     label: '52 Week High',    cls: 'num mono', get: r => seededNum(r.symbol, 3, r.mark * 1.05, r.mark * 1.6), format: v => fmtNum(v) },
  { id: 'chg20d',     label: '20 Day % Chg',    cls: 'num',      get: r => seededNum(r.symbol, 4, -15, 15), format: fmtPct },
  { id: 'chg5d',      label: '5 Day % Chg',     cls: 'num',      get: r => seededNum(r.symbol, 5, -8, 8), format: fmtPct },
  { id: 'chg6m',      label: '6 Month % Chg',   cls: 'num',      get: r => seededNum(r.symbol, 6, -25, 45), format: fmtPct },
  { id: 'chg1m',      label: '1 Month % Chg',   cls: 'num',      get: r => seededNum(r.symbol, 7, -12, 20), format: fmtPct },
  { id: 'beta',       label: 'Beta',            cls: 'num',      get: r => r.beta ?? seededNum(r.symbol, 8, 0.4, 1.8), format: v => fmtNum(v, 2) },
  { id: 'bidSize',    label: 'Bid Size',        cls: 'num',      get: r => r.bidSize ?? seededInt(r.symbol, 11, 100, 5000), format: v => typeof v === 'number' ? v.toLocaleString('en-NG') : '-' },
  { id: 'askSize',    label: 'Ask Size',        cls: 'num',      get: r => r.askSize ?? seededInt(r.symbol, 12, 100, 5000), format: v => typeof v === 'number' ? v.toLocaleString('en-NG') : '-' },
  { id: 'dailyHigh',  label: 'Daily High',      cls: 'num mono', get: r => r.dailyHigh ?? r.mark * (1 + Math.abs(r.pctChange) / 100 + 0.005), format: v => fmtNum(v) },
  { id: 'dailyLow',   label: 'Daily Low',       cls: 'num mono', get: r => r.dailyLow ?? r.mark * (1 - Math.abs(r.pctChange) / 100 - 0.005), format: v => fmtNum(v) },
  { id: 'description',label: 'Description',     cls: 'text',     get: r => r.description, format: fmtStr },
  { id: 'divAmt',     label: 'Dividend Amt',    cls: 'num mono', get: r => r.dividendAmt ?? seededNum(r.symbol, 13, 0, 8), format: fmtNaira },
  { id: 'eps',        label: 'EPS',             cls: 'num mono', get: r => r.eps ?? seededNum(r.symbol, 14, -2, 18), format: v => fmtNum(v) },
  { id: 'industry',   label: 'Industry',        cls: 'text',     get: r => r.industry ?? guessSector(r.symbol), format: fmtStr },
  { id: 'last',       label: 'Last',            cls: 'num mono', get: r => r.last ?? r.mark, format: v => fmtNum(v) },
  { id: 'markChgN',   label: 'Mark Chg \u20A6', cls: 'num',      get: r => r.mark * (r.pctChange / 100), format: v => typeof v === 'number' ? (v >= 0 ? '+' : '-') + '\u20A6' + Math.abs(v).toFixed(2) : '-' },
  { id: 'marketCap',  label: 'Market Cap \u20A6B', cls: 'num',   get: r => seededNum(r.symbol, 15, 2, 800), format: v => typeof v === 'number' ? '\u20A6' + v.toFixed(1) + 'B' : '-' },
  { id: 'pe',         label: 'PE',              cls: 'num mono', get: r => r.pe ?? seededNum(r.symbol, 16, 4, 42), format: v => fmtNum(v, 2) },
  { id: 'sector',     label: 'Sector',          cls: 'text',     get: r => r.sector ?? guessSector(r.symbol), format: fmtStr },
  { id: 'shares',     label: 'Shares',          cls: 'num',      get: r => r.shares ?? seededInt(r.symbol, 17, 100_000_000, 40_000_000_000), format: fmtVol },
  { id: 'ystClose',   label: 'Yesterday Close', cls: 'num mono', get: r => r.yesterdayClose ?? r.mark / (1 + r.pctChange / 100), format: v => fmtNum(v) },
  { id: 'ytdPct',     label: 'YTD % Chg',       cls: 'num',      get: r => seededNum(r.symbol, 18, -30, 60), format: fmtPct },
  { id: 'yield',      label: 'Yield',           cls: 'num',      get: r => r.yield ?? seededNum(r.symbol, 19, 0, 12), format: v => typeof v === 'number' ? v.toFixed(2) + '%' : '-' },
  { id: 'approxBor',  label: 'Approx Borrow',   cls: 'num',      get: r => r.approxBorrow ?? seededNum(r.symbol, 20, 0, 8), format: v => typeof v === 'number' ? v.toFixed(2) + '%' : '-' },
  { id: 'daysToExp',  label: 'Days to Exp',     cls: 'num',      get: r => r.daysToExp, format: fmtStr },
  { id: 'delta',      label: 'Delta (\u0394)',  cls: 'num',      get: r => r.delta, format: fmtStr },
  { id: 'epr',        label: 'EPR',             cls: 'num',      get: r => r.epr, format: fmtStr },
  { id: 'exDate',     label: 'Ex Date',         cls: 'text',     get: r => r.exDate, format: fmtStr },
  { id: 'gamma',      label: 'Gamma (\u0393)',  cls: 'num',      get: r => r.gamma, format: fmtStr },
  { id: 'htb',        label: 'HTB/ETB',         cls: 'text',     get: r => r.htbEtb ?? (seedh(r.symbol, 21) % 7 === 0 ? 'HTB' : 'ETB'), format: fmtStr },
  { id: 'initMargin', label: 'Initial Margin',  cls: 'num',      get: r => r.initialMargin ?? seededNum(r.symbol, 22, 25, 60), format: v => typeof v === 'number' ? v.toFixed(0) + '%' : '-' },
  { id: 'ivPct',      label: 'IV Percentile',   cls: 'num',      get: r => r.ivPercentile ?? seededNum(r.symbol, 23, 0, 100), format: v => typeof v === 'number' ? v.toFixed(0) : '-' },
  { id: 'ltradeDate', label: 'Last Trade Date', cls: 'text',     get: r => r.lastTradeDate ?? new Date().toISOString().slice(0, 10), format: fmtStr },
  { id: 'mmm',        label: 'Mkt Maker Move',  cls: 'num',      get: r => r.mktMakerMove, format: fmtStr },
  { id: 'openInt',    label: 'Open Interest',   cls: 'num',      get: r => r.openInterest, format: fmtStr },
  { id: 'payDate',    label: 'Pay Date',        cls: 'text',     get: r => r.payDate, format: fmtStr },
  { id: 'pcr',        label: 'Put/Call Ratio',  cls: 'num',      get: r => r.putCallRatio ?? seededNum(r.symbol, 24, 0.2, 3.2), format: v => fmtNum(v, 2) },
  { id: 'rho',        label: 'Rho',             cls: 'num',      get: r => r.rho, format: fmtStr },
  { id: 'shortBor',   label: 'Short Sale Borr', cls: 'num',      get: r => r.shortSaleBorrow ?? seededNum(r.symbol, 25, 0.5, 12), format: v => typeof v === 'number' ? v.toFixed(2) + '%' : '-' },
  { id: 'subIndustry',label: 'Sub-Industry',    cls: 'text',     get: r => r.subIndustry ?? guessSector(r.symbol), format: fmtStr },
  { id: 'theta',      label: 'Theta (\u0398)',  cls: 'num',      get: r => r.theta, format: fmtStr },
  { id: 'upcomingE',  label: 'Upcoming Earnings', cls: 'text',   get: r => r.upcomingEarnings ?? futureDate(seededInt(r.symbol, 26, 5, 90)), format: fmtStr },
  { id: 'vega',       label: 'Vega (\u03BD)',   cls: 'num',      get: r => r.vega, format: fmtStr },
];

const DEFAULT_VISIBLE = [
  'mark', 'markChgPct', 'volume', 'bid', 'ask', 'iv',
  'low52', 'high52', 'chg20d', 'chg5d', 'chg6m',
];

const VISIBLE_KEY = 'nigastock.watchlist.cols';

function loadVisible(): string[] {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(VISIBLE_KEY) : null;
    if (!raw) return DEFAULT_VISIBLE;
    const arr = JSON.parse(raw) as string[];
    if (!Array.isArray(arr) || arr.length === 0) return DEFAULT_VISIBLE;
    return arr.filter((id) => WATCHLIST_COLUMNS.some((c) => c.id === id));
  } catch { return DEFAULT_VISIBLE; }
}
function saveVisible(ids: string[]) {
  try { localStorage.setItem(VISIBLE_KEY, JSON.stringify(ids)); } catch {}
}

function guessSector(sym: string): string {
  const s = sym.toUpperCase();
  if (s.endsWith('BANK') || s.startsWith('GT') || s.includes('FIRST') || s.includes('ZENITH')) return 'Financials';
  if (s.includes('CEM') || s.includes('BUA')) return 'Materials';
  if (s.includes('MTN') || s.includes('AIRTEL')) return 'Telecom';
  if (s.includes('SEPLAT') || s.includes('ARADEL') || s.includes('GEREGU') || s.includes('TRANS')) return 'Energy';
  if (s.includes('NESTLE') || s.includes('FOOD') || s.includes('PRESCO')) return 'Consumer Staples';
  return 'Industrials';
}
function futureDate(days: number): string {
  const d = new Date(Date.now() + days * 86400_000);
  return d.toISOString().slice(0, 10);
}

export interface WatchlistGroup {
  id: string;
  name: string;
  symbols?: string[];
}
const WL_GROUPS_KEY = 'nigastock.watchlist.groups';
const WL_ACTIVE_KEY = 'nigastock.watchlist.active';
export const MAX_WATCHLISTS = 10;

function defaultGroups(universe: string[]): WatchlistGroup[] {
  return [{ id: 'ngx-default', name: 'NGX Default', symbols: universe }];
}
function loadGroups(universe: string[]): WatchlistGroup[] {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(WL_GROUPS_KEY) : null;
    if (!raw) return defaultGroups(universe);
    const arr = JSON.parse(raw) as WatchlistGroup[];
    if (!Array.isArray(arr) || arr.length === 0) return defaultGroups(universe);
    return arr.slice(0, MAX_WATCHLISTS);
  } catch { return defaultGroups(universe); }
}
function saveGroups(groups: WatchlistGroup[]) {
  try { localStorage.setItem(WL_GROUPS_KEY, JSON.stringify(groups)); } catch {}
}

export function Watchlist({ rows, selected, onSelect }: Props) {
  const [q, setQ] = useState('');
  const [visible, setVisible] = useState<string[]>(() => loadVisible());
  const [showColumns, setShowColumns] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const universe = useMemo(() => rows.map((r) => r.symbol), [rows]);
  const [groups, setGroups] = useState<WatchlistGroup[]>(() => loadGroups(universe));
  const [activeId, setActiveId] = useState<string>(() => {
    try { return (typeof localStorage !== 'undefined' ? localStorage.getItem(WL_ACTIVE_KEY) : null) ?? 'ngx-default'; } catch { return 'ngx-default'; }
  });
  const [showNewList, setShowNewList] = useState(false);

  useEffect(() => { saveVisible(visible); }, [visible]);
  useEffect(() => { saveGroups(groups); }, [groups]);
  useEffect(() => { try { localStorage.setItem(WL_ACTIVE_KEY, activeId); } catch {} }, [activeId]);
  useEffect(() => {
    if (!groups.find((g) => g.id === activeId) && groups[0]) setActiveId(groups[0].id);
  }, [groups, activeId]);

  const activeGroup = groups.find((g) => g.id === activeId) ?? groups[0];
  const activeSymbolSet = activeGroup?.symbols && activeGroup.symbols.length > 0
    ? new Set(activeGroup.symbols) : undefined;

  const filtered = useMemo(() => {
    const term = q.trim().toUpperCase();
    let r = rows;
    if (activeSymbolSet) r = r.filter((x) => activeSymbolSet.has(x.symbol));
    if (term) r = r.filter((x) => x.symbol.includes(term));
    return r;
  }, [q, rows, activeSymbolSet]);

  const addWatchlist = () => {
    if (groups.length >= MAX_WATCHLISTS) {
      alert('You can save up to ' + MAX_WATCHLISTS + ' watchlists. Delete one to make room.');
      return;
    }
    setShowNewList(true);
  };
  const createGroup = (name: string, symbols: string[]) => {
    const id = 'wl-' + Date.now().toString(36);
    const next = [...groups, { id, name, symbols } as WatchlistGroup];
    setGroups(next);
    setActiveId(id);
    setShowNewList(false);
  };
  const deleteGroup = (id: string) => {
    if (groups.length <= 1) { alert('You must keep at least one watchlist.'); return; }
    if (!confirm('Delete this watchlist?')) return;
    const next = groups.filter((g) => g.id !== id);
    setGroups(next);
    if (activeId === id) setActiveId(next[0]!.id);
  };

  const activeCols = useMemo(
    () => visible.map((id) => WATCHLIST_COLUMNS.find((c) => c.id === id)!).filter(Boolean),
    [visible],
  );
  const columnsToRender = expanded ? WATCHLIST_COLUMNS : activeCols;
  const gridTemplate = '110px ' + columnsToRender.map((c) => c.cls === 'text' ? 'minmax(120px,1.3fr)' : '100px').join(' ');

  return (
    <section className={'watchlist ' + (expanded ? 'wl-expanded' : '')} aria-label="Watchlist">
      <div className="head">
        <h3>Watchlist</h3>
        <div className="actions">
          <button
            type="button"
            className="wl-icon"
            title={expanded ? 'Collapse watchlist' : 'Expand watchlist (show all columns)'}
            aria-label={expanded ? 'Collapse' : 'Expand'}
            onClick={() => setExpanded((v) => !v)}
          >{expanded ? '\u2922' : '\u2921'}</button>
          <a href="#" onClick={(e) => { e.preventDefault(); setShowColumns(true); }}>COLUMNS</a>
          <a href="#" onClick={(e) => { e.preventDefault(); addWatchlist(); }}>+ ADD</a>
        </div>
      </div>

      <div className="wl-tabs" role="tablist" aria-label="Watchlists">
        {groups.map((g) => (
          <div key={g.id} className={'wl-tab ' + (g.id === activeId ? 'active' : '')} role="tab" aria-selected={g.id === activeId}>
            <button type="button" className="wl-tab-name" onClick={() => setActiveId(g.id)} title={'Switch to ' + g.name}>
              {g.name} {g.symbols ? <span className="wl-tab-count">({g.symbols.length})</span> : null}
            </button>
            {groups.length > 1 && (
              <button type="button" className="wl-tab-x" aria-label={'Delete ' + g.name} title="Delete" onClick={() => deleteGroup(g.id)}>&times;</button>
            )}
          </div>
        ))}
        <button type="button" className="wl-tab-add" title={groups.length >= MAX_WATCHLISTS ? 'Limit reached (' + MAX_WATCHLISTS + ')' : 'New watchlist'} onClick={addWatchlist} disabled={groups.length >= MAX_WATCHLISTS}>+</button>
      </div>

      <div className="search" role="search">
        <span aria-hidden>&#128269;</span>
        <input
          aria-label="Filter watchlist"
          placeholder="Search symbol or name"
          value={q}
          onChange={(e) => setQ(e.target.value.toUpperCase())}
        />
      </div>
      <div className="grid wl-grid" role="grid">
        <div className="row-h" role="row" style={{ gridTemplateColumns: gridTemplate }}>
          <div role="columnheader">Symbol</div>
          {columnsToRender.map((c) => (
            <div key={c.id} role="columnheader" className={c.cls}>{c.label}</div>
          ))}
        </div>
        {filtered.map((r) => (
          <div
            key={r.symbol}
            role="row"
            tabIndex={0}
            className={'row ' + (selected === r.symbol ? 'active' : '')}
            style={{ gridTemplateColumns: gridTemplate }}
            onClick={() => onSelect(r.symbol)}
            onKeyDown={(e) => { if (e.key === 'Enter') onSelect(r.symbol); }}
          >
            <div className="sym">{r.symbol}</div>
            {columnsToRender.map((c) => {
              const v = c.get(r);
              return (
                <div key={c.id} className={c.cls}>
                  {c.format ? c.format(v, r) : (v ?? '-')}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {showColumns && (
        <ColumnsModal visible={visible} onChange={setVisible} onClose={() => setShowColumns(false)} />
      )}
      {showNewList && (
        <NewListModal
          universe={universe}
          existingNames={groups.map((g) => g.name)}
          onCreate={createGroup}
          onClose={() => setShowNewList(false)}
        />
      )}
    </section>
  );
}

function NewListModal({ universe, existingNames, onCreate, onClose }: {
  universe: string[];
  existingNames: string[];
  onCreate: (name: string, symbols: string[]) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [q, setQ] = useState('');

  const toggle = (s: string) => setPicked((p) => p.includes(s) ? p.filter((x) => x !== s) : [...p, s]);
  const filtered = universe.filter((s) => s.toUpperCase().includes(q.trim().toUpperCase()));
  const trimmed = name.trim();
  const nameErr = !trimmed
    ? 'Enter a name for this watchlist.'
    : existingNames.some((n) => n.toLowerCase() === trimmed.toLowerCase())
      ? 'A watchlist with that name already exists.'
      : undefined;

  const submit = () => {
    if (nameErr) return;
    if (picked.length === 0) { if (!confirm('Create an empty watchlist?')) return; }
    onCreate(trimmed, picked);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="wl-modal-backdrop" role="dialog" aria-modal="true" aria-label="Create Watchlist">
      <div className="wl-modal" style={{ maxWidth: 520 }}>
        <div className="wl-modal-h">
          <strong>Create Watchlist</strong>
          <div className="wl-modal-actions">
            <button type="button" className="wl-close" aria-label="Close" onClick={onClose}>&times;</button>
          </div>
        </div>
        <div className="wl-new-body">
          <label className="wl-new-label" htmlFor="wl-new-name">Name</label>
          <input
            id="wl-new-name"
            className="wl-new-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Banks, My Swing Set"
            maxLength={40}
            autoFocus
          />
          {nameErr && <div className="wl-new-err">{nameErr}</div>}

          <label className="wl-new-label" htmlFor="wl-new-search">Add Symbols ({picked.length})</label>
          <input
            id="wl-new-search"
            className="wl-new-input"
            value={q}
            onChange={(e) => setQ(e.target.value.toUpperCase())}
            placeholder="Search NGX symbols"
          />
          <div className="wl-new-symbols">
            {filtered.map((sym) => (
              <button
                key={sym}
                type="button"
                className={'wl-new-sym ' + (picked.includes(sym) ? 'on' : '')}
                onClick={() => toggle(sym)}
              >{picked.includes(sym) ? '✓ ' : ''}{sym}</button>
            ))}
            {filtered.length === 0 && <div className="wl-empty">No matching symbols.</div>}
          </div>
        </div>
        <div className="wl-modal-foot">
          <button type="button" className="wl-cancel" onClick={onClose}>Cancel</button>
          <button type="button" className="wl-save" onClick={submit} disabled={!!nameErr}>Create</button>
        </div>
      </div>
    </div>
  );
}

function ColumnsModal({ visible, onChange, onClose }: {
  visible: string[];
  onChange: (next: string[]) => void;
  onClose: () => void;
}) {
  const [local, setLocal] = useState<string[]>(visible);

  const visibleDefs = local.map((id) => WATCHLIST_COLUMNS.find((c) => c.id === id)).filter(Boolean) as ColDef[];
  const availableDefs = WATCHLIST_COLUMNS.filter((c) => !local.includes(c.id));

  const add = (id: string) => { if (!local.includes(id)) setLocal([...local, id]); };
  const remove = (id: string) => setLocal(local.filter((x) => x !== id));
  const move = (id: string, dir: -1 | 1) => {
    const i = local.indexOf(id); if (i < 0) return;
    const j = i + dir; if (j < 0 || j >= local.length) return;
    const copy = local.slice(); [copy[i], copy[j]] = [copy[j]!, copy[i]!]; setLocal(copy);
  };
  const reset = () => setLocal(DEFAULT_VISIBLE);
  const save = () => { onChange(local); onClose(); };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="wl-modal-backdrop" role="dialog" aria-modal="true" aria-label="Customize Watchlist Columns">
      <div className="wl-modal">
        <div className="wl-modal-h">
          <strong>Customize Watchlist Columns</strong>
          <div className="wl-modal-actions">
            <button type="button" className="wl-reset" onClick={reset}>Reset</button>
            <button type="button" className="wl-close" aria-label="Close" onClick={onClose}>&times;</button>
          </div>
        </div>
        <div className="wl-modal-body">
          <div className="wl-col">
            <div className="wl-col-h">Visible Columns</div>
            <div className="wl-col-list">
              {visibleDefs.map((c) => (
                <div key={c.id} className="wl-chip visible">
                  <button className="wl-chip-btn remove" onClick={() => remove(c.id)} aria-label={'Remove ' + c.label} title="Remove">&minus;</button>
                  <span className="wl-chip-label">{c.label}</span>
                  <span className="wl-chip-order">
                    <button className="wl-chip-ord" onClick={() => move(c.id, -1)} aria-label="Move up" title="Move up">&#x25B4;</button>
                    <button className="wl-chip-ord" onClick={() => move(c.id, 1)} aria-label="Move down" title="Move down">&#x25BE;</button>
                  </span>
                </div>
              ))}
              {visibleDefs.length === 0 && (
                <div className="wl-empty">No visible columns. Reset to restore defaults.</div>
              )}
            </div>
          </div>
          <div className="wl-col">
            <div className="wl-col-h">Available Columns</div>
            <div className="wl-col-list available-grid">
              {availableDefs.map((c) => (
                <div key={c.id} className="wl-chip available">
                  <button className="wl-chip-btn add" onClick={() => add(c.id)} aria-label={'Add ' + c.label} title="Add">+</button>
                  <span className="wl-chip-label">{c.label}</span>
                </div>
              ))}
              {availableDefs.length === 0 && (
                <div className="wl-empty">All columns are already visible.</div>
              )}
            </div>
          </div>
        </div>
        <div className="wl-modal-foot">
          <button type="button" className="wl-cancel" onClick={onClose}>Cancel</button>
          <button type="button" className="wl-save" onClick={save}>Apply</button>
        </div>
      </div>
    </div>
  );
}
